import React, { useId } from 'react';
import PropTypes from 'prop-types';

export const FormInput = React.forwardRef(({
  label,
  icon: Icon,
  type = 'text',
  placeholder,
  value,
  defaultValue,
  onChange,
  disabled,
  extra = null,
  rightIcon: RightIcon = null,
  onRightIconClick,
  tabIndex,
  dark = false,
  name,
  autoComplete,
  id: idProp,
  required = false,
}, ref) => {
  const generatedId = useId();
  const inputId = idProp || `input-${generatedId}`;
  const labelClass = `text-[11px] font-semibold uppercase tracking-[0.1em] ${dark ? 'text-gray-400' : 'text-gray-700'}`;
  const iconClass = `absolute left-3.5 top-1/2 -translate-y-1/2 ${dark ? 'text-gray-500' : 'text-gray-700'} group-focus-within:text-[var(--theme-primary)] transition-colors pointer-events-none`;
  const inputClass = `w-full ${dark ? 'bg-[#0d0d0d] border-white/10 text-white placeholder:text-gray-600 focus:bg-[#222]' : 'bg-gray-50 border-gray-100 text-[#0d0d0d] focus:bg-white'} border group-hover:border-[var(--theme-primary-tint)] focus:border-[var(--theme-primary)] rounded-sm py-2 pl-9 ${RightIcon ? 'pr-10' : 'pr-3'} outline-none transition-all text-xs shadow-sm disabled:opacity-60 disabled:cursor-not-allowed`;

  return (
    <div className="space-y-0.5 w-full text-left">
      <div className="flex justify-between items-center px-1">
        <label htmlFor={inputId} className={labelClass}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {extra}
      </div>
      <div className="relative group">
        <div className={iconClass} aria-hidden="true">
          <Icon size={13} />
        </div>
        <input
          ref={ref}
          id={inputId}
          name={name}
          type={type}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          disabled={disabled}
          autoComplete={autoComplete}
          aria-label={label}
          className={inputClass}
          tabIndex={tabIndex}
          required={required}
        />
        {RightIcon && (
          <button
            type="button"
            tabIndex={-1}
            onClick={onRightIconClick}
            aria-label={`Toggle ${label}`}
            className={`absolute right-3 top-1/2 -translate-y-1/2 ${dark ? 'text-gray-500' : 'text-gray-700'} hover:text-[var(--theme-primary)] transition-colors outline-none`}
          >
            <RightIcon size={14} />
          </button>
        )}
      </div>
    </div>
  );
});

FormInput.displayName = 'FormInput';

FormInput.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  extra: PropTypes.node,
  rightIcon: PropTypes.elementType,
  onRightIconClick: PropTypes.func,
  tabIndex: PropTypes.number,
  dark: PropTypes.bool,
  name: PropTypes.string,
  autoComplete: PropTypes.string,
  id: PropTypes.string,
  required: PropTypes.bool,
};
